<template>
  <div class="cart">
    <div v-if="!loading">
      <v-container class="pa-4">
        <TitleChip title="Carrinhos abandonados" />
        <v-layout class="d-flex column">
          <v-flex class="animated fadeIn delay-1s">
            <v-text-field
              v-model="search"
              class="px-2 mt-2 search-autocomplete"
              color="primary"
              placeholder="Busque por CPF"
              prepend-icon="$vuetify.icons.searchIcon"
            />
          </v-flex>
        </v-layout>

        <div
          v-for="([date, checkoutsGrouped], index) in Object.entries(groupDate)"
          :key="`${index}--${date}`"
        >
          <v-layout
            row
            class="mt-3 date"
          >
            {{ date }}
          </v-layout>
          <v-card
            v-for="checkout in checkoutsGrouped"
            :key="checkout._id"
            v-ripple
            flat
            class="py-4 px-3 cart-card"
            height="fit-content"
            @click="proceedToCheckout(checkout)"
          >
            <v-card-title class="cart-card__title mb-3">
              <div>
                Pedido nº {{ checkout._id }}
              </div>
              <div>
                {{ checkout.createdAt | filtraData }}
              </div>
            </v-card-title>
            <v-card-text class="cart-card__content">
              {{ getDocType(checkout.person.type) }}:  {{ checkout.person.cpf ? checkout.person.cpf : 'Não informado' }} <br>
              Total do carrinho: {{ getCartTotal(checkout) | currency }} <br>
              <v-icon
                color="#140C22"
                medium
                style="align-items: end;"
              >
                mdi-arrow-right
              </v-icon>
            </v-card-text>
          </v-card>
        </div>
      </v-container>
    </div>
    <div v-else>
      <Loader class="mt-3" />
    </div>
  </div>
</template>

<script>
import { everything } from '@sejazipper/layer-api-client';
import format from 'date-fns/format';
import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';
import { mapGetters } from 'vuex';
import { useZipperApi } from '../composables/useZipperApi';

const dayjs = require('dayjs');

const { client } = useZipperApi();

export default {
  filters: {
    filtraData(value) {
      if (value) {
        return format(value, 'DD/MM/YY');
      }

      return 0;
    },
  },

  data() {
    return {
      store: null,
      products: null,
      itemIds: [],
      contato: null,
      checkouts: null,
      seller: null,
      loading: false,
      selected: null,
      search: '',
    };
  },

  computed: {
    ...mapGetters(['getCustomer', 'getStore']),

    groupDate() {
      const orderCheckout = orderBy(this.filteredCheckout, ['createdAt'], ['desc']);
      const sevenDaysBefore = dayjs().subtract(7, 'days');
      const filteredLastOrders = orderCheckout.filter((d) => dayjs(d.createdAt).isAfter(sevenDaysBefore));
      return groupBy(filteredLastOrders, (c) => format(c.createdAt, 'DD/MM'));
    },

    filteredCheckout() {
      if (!this.search) return this.checkouts;
      const checkoutFiltered = this.checkouts.filter((checkout) => !!checkout.person.cpf && checkout.person.cpf.includes(this.search));
      return checkoutFiltered;
    },
  },

  async mounted() {
    this.store = await client.chain.query.findStoreByMongoId({
      mongoId: this.getStore.id,
    }).get({ ...everything });

    await this.getCheckoutsBySellerIdAndCustomerId();
    await this.getProducts();
    this.updateProducts();
  },

  methods: {
    async getCheckoutsBySellerIdAndCustomerId() {
      try {
        this.loading = true;
        const checkouts = await client.chain.query
          .getCheckoutsBySellerIdAndCustomerId({
            sellerId: this.$auth.user.sub,
            customerId: this.getCustomer.id,
          })
          .get({ ...everything, items: { ...everything, sku: { ...everything } }, person: { ...everything } });

        this.checkouts = checkouts.filter((cart) => !!cart.abandoned);

        this.itemIds = this.checkouts.flatMap((checkout) => checkout.items.map((item) => Number(item.idERP)));
        console.log('checkouts', this.checkouts);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async getProducts() {
      this.loading = true;
      this.products = await client.chain.query
        .findOMSProductsByIds({ productIds: this.itemIds })
        .get({ ...everything, skus: { ...everything } });

      this.loading = false;
    },

    async updateProducts() {
      this.loading = true;

      const updatedCheckouts = this.checkouts.map((checkout) => {
        const updatedItems = checkout.items.map((item) => {
          const skuId = item.sku.sku_id;
          const productToAdd = this.products.find((product) => product.skus.some((sku) => sku.id === skuId));
          if (productToAdd) {
            const skuSelected = productToAdd.skus.find((sku) => sku.id === skuId);
            const currentStock = skuSelected.sku_stocks.find((stock) => stock.store_id === this.store);
            const product = {
              ...productToAdd,
              ean: skuSelected.ean,
              size: skuSelected.size,
              color: skuSelected.color,
              reference: skuSelected.reference,
              sku_id: skuSelected.id,
              skuId: `${skuSelected.size || ''}${skuSelected.color || ''}`,
              old_price: item.oldPrice,
              price: item.price,
              currentStock: currentStock ? currentStock.stock : 0,
              quantity: item.quantity,
            };
            return product;
          }
          return item;
        });

        return { ...checkout, items: updatedItems };
      });

      this.checkouts = updatedCheckouts;
      this.loading = false;
    },

    getDocType(type) {
      switch (type) {
        case 'PF':
          return 'CPF do cliente';
        case 'PJ':
          return 'CNPJ do cliente';
        default:
          return 'Documento estrangeiro';
      }
    },

    getCartTotal(checkout) {
      const totalItems = checkout.items.reduce((acc, curr) => {
        if (curr.skus && curr.skus.length) {
          const skuPrice = curr.skus[0].sku_prices.find((sku) => sku.store_id === this.store.id);

          if (skuPrice) {
            return acc + skuPrice.price;
          }
        }
        return acc;
      }, 0);

      return totalItems || 0;
    },

    proceedToCheckout(checkout) {
      const { items } = checkout;
      items.forEach((item) => {
        this.checkIfProductHasModification(item);
      });

      this.$store.dispatch('clearCheckout');
      this.$store.dispatch('addProductsToCatalogue', items);
      this.$router.push({ name: 'catalogo', params: { checkout } });
    },

    checkIfProductHasModification(cartItem) {
      if (!cartItem.sku) return;

      const productInStock = this.products.find(
        (prod) => prod.skus[0].sku_stocks.some(
          (stock) => stock.sku_id === cartItem.sku.sku_id
            && stock.store_id === this.store.id
            && stock.stock >= 1,
        ),
      );

      if (!productInStock) this.$set(cartItem, 'modification', 'zeroStock');
    },
  },
};

</script>

<style lang="scss" scoped>
.cart {
  padding-bottom: 80px;
}
.cart-card {
  font-family: 'Nunito Sans';
  border-radius: 8px;
  height: 42px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  &__title {
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    padding: 0;
    font-size: 16px;
  }
  &__content {
    display: flex;
    justify-content: space-between;
    padding: 0;
    font-size: 14px;
    font-weight: 600;
  }
}
.search-autocomplete {
  font-family: 'Neue-Metana' !important;
  font-size: 12px;
  line-height: 102%;
  font-weight: normal !important;
}
.date {
  font-weight: 700;
  font-size: 20px;
  line-height: 27px;
  font-family: 'Nunito Sans';
  color: rgba(0, 0, 0, 0.5);
}
</style>
